import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import { useUser, useCompany } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useExportIssuesMutations } from './mutations.hooks'
import type { ExportIssueForm, ExportIssuesMassForm } from './types'
import { useCallback } from 'react'
import type { InfringementActionLog } from '@/modules/infringements/types.graphl'
import type { IssueActionLog } from './types.graphl'

export function useIssueExportForm(id: string, onCompleted?: () => void) {
  const me = useUser()

  const { exportIssue, loading: mailLoading } =
    useExportIssuesMutations(onCompleted)

  const {
    formValues: form,
    submitHandler: submit,
    updateForm,
  } = useForm<ExportIssueForm>({
    values: {
      recipients: [],
      exportOption: ExportOption.DOWNLOAD,
      exportType: ExportType.ACTIVITIES,
      exportFormat: ExportFormat.PDF,
    },
  })

  const handler = useCallback(
    <K extends keyof ExportIssueForm>(key: K) => {
      return (value: ExportIssueForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const handleExport = useCallback(
    async (values: ExportIssueForm) => {
      void exportIssue({
        variables: {
          userId: me.id,
          reportId: id,
          recipients: values.recipients,
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          exportOption: values.exportOption,
        },
      })
    },
    [exportIssue, id, me.id]
  )

  return {
    form,
    loading: mailLoading,
    mailLoading,

    handler,
    updateForm,
    submit,
    handleExport,
  }
}

export function useMassIssueExportForm(onCompleted?: () => void) {
  const me = useUser()
  const company = useCompany()

  const { exportIssuesMass, loading: formLoading } =
    useExportIssuesMutations(onCompleted)

  const {
    formValues: form,
    submitHandler: submit,
    updateForm,
  } = useForm<ExportIssuesMassForm>({
    values: {
      recipients: [],
      dateStart: new Date(),
      dateEnd: new Date(),
      month: new Date(),
      exportType: ExportType.ISSUES,
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      user: null,
      includeArchived: false,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-issues-mass-form',
        excludeFields: ['recipients'],
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
        },
      },
    },
  })

  function handler<K extends keyof ExportIssuesMassForm>(key: K) {
    return (value: ExportIssuesMassForm[K] | null) => {
      if (value === null) return
      updateForm({ [key]: value })
    }
  }

  async function handleExport(values: ExportIssuesMassForm) {
    const date = form.useDateRange ? form.dateStart : startOfMonth(values.month)
    const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)

    void exportIssuesMass({
      variables: {
        recipients: values.recipients,
        exportType: values.exportType,
        exportFormat: values.exportFormat,
        userId: me.id,
        companyId: company.id,
        dateStart: dateStart,
        dateEnd: dateEnd,
        exportOption: values.exportOption,
        includeArchived: values.includeArchived,
      },
    })
  }

  function getDateInterval(startDate: Date, endDate?: Date): [string, string] {
    if (form.useDateRange) {
      const dateEnd = endDate || new Date()
      return [formatISO(startDate), formatISO(dateEnd)]
    }
    return [
      formatISO(startOfMonth(startDate)),
      formatISO(endOfMonth(startDate)),
    ]
  }

  return {
    form,
    loading: formLoading,
    handler,
    updateForm,
    submit,
    handleExport,
    getDateInterval,
  }
}

/**
 * Generates a localized string describing an action performed by a user.
 * Used for both issue and infringement action logs (see `InfringementsTable`),
 * hence the union parameter.
 *
 * If the action involves a secondary user (e.g., a reminder), the string
 * will include both the primary and secondary users' names.
 */
export function useIssueActionString(
  node: Pick<
    IssueActionLog | InfringementActionLog,
    'user' | 'secondaryUser' | 'action'
  >
) {
  const translations = useTranslate({
    actionLog: {
      CREATED: ['issues.created-by-user', { user: 'user' }],
      UPDATED: ['issues.updated-by-user', { user: 'user' }],

      ARCHIVED: ['issues.archived-by-user', { user: 'user' }],
      RESTORED: ['issues.restored-by-user', { user: 'user' }],

      CLOSED: ['issues.closed-by-user', { user: 'user' }],
      REOPENED: ['issues.reopened-by-user', { user: 'user' }],

      EXPORTED: ['issues.exported-by-user', { user: 'user' }],

      RECEIPTED: ['issues.receipted-by-user', { user: 'user' }],

      LEADER_COMMENT_UPDATED: [
        'issues.leader-comment-updated-by-user',
        { user: 'user' },
      ],

      REMINDER_SENT: [
        'issues.reminder-by-user-to-user',
        { user: 'user', target: 'target' },
      ],
    },
  })

  const user = node.user ? node.user.fullName : 'System'
  const secondaryUser = node.secondaryUser ? node.secondaryUser.fullName : ''
  // Only REMINDER_SENT renders the "by X to Y" string. Keying off the mere
  // presence of secondaryUser would mislabel any other action that carries one.
  if (node.action === 'REMINDER_SENT') {
    return translations.actionLog.REMINDER_SENT({
      user: user,
      target: secondaryUser,
    })
  }

  const translate = translations.actionLog[node.action]
  return translate ? translate({ user: user }) : ''
}
